import {
  DFDChat,
  DFDContentFormat,
  DFDError,
  type ChatSession,
  type DFDBotEvent,
  type DFDActionsUpdatedEvent,
} from 'thconnect-dfd-web-sdk';
import {
  ErrorTypes,
  EventTypes,
  AgentChatEvents,
  UserChatEvents,
  LiveChatClient,
  ChatEndedReasonTypes,
} from '@leagueplatform/live-chat';
import { captureError } from '@leagueplatform/observability';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { getUserProfile } from '@leagueplatform/user-profile-api';
import { getIdentityToken } from 'utils/identity-token';
import AI_Guide_Avatar from '../assets/dfd-bot-avatar.svg';

/**
 * League event mapping follows a fatal vs recoverable split:
 * - Fatal: `CHAT_ENDED` + `CHAT_UNAVAILABLE` — user must leave / reopen; we clear SDK refs.
 * - Recoverable: `ERROR_RECEIVED` + `MESSAGE_FAILED` (retry) or `DEFAULT` — chat may continue.
 */

type DfdAction = DFDActionsUpdatedEvent['actions'][number];

const AI_AGENT_ID = 'dfd-chatbot';
const AI_AGENT_NAME = 'Chatbot';
const AI_AGENT_AVATAR_URL = AI_Guide_Avatar;

export interface DFDLiveChatSettings {
  onChatEnd?: () => void;
}

export const createLiveChatClient = ({
  onChatEnd,
}: DFDLiveChatSettings = {}): LiveChatClient => {
  const dfdHostUrl = import.meta.env.VITE_DFD_HOST_URL;
  const dfdId = import.meta.env.VITE_DFD_ID;

  let currentSession: ChatSession | null = null;
  /** FIFO: League `eventId`s waiting for `MessageAcknowledged` (pair with correlationId in order). */
  const pendingEventIdQueue: string[] = [];
  /** Maps DFD `correlationId` (from MessageAcknowledged) → League user message `eventId` for retry. */
  const correlationIdToEventOriginId = new Map<string, string>();

  let storedHandler: ((event: AgentChatEvents) => void) | null = null;
  /** Set when we emit `CHAT_STARTED` for the current DFD session (`SessionStarted` / `SessionRestarted`). */
  let leagueChatStarted = false;

  const enqueuePendingEventId = (eventId: string) => {
    pendingEventIdQueue.push(eventId);
  };

  const shouldUseAnonymousSession = async (): Promise<boolean> => {
    try {
      const isAuthenticated = await StandaloneAuth.client.isAuthenticated();
      return !isAuthenticated;
    } catch {
      return true;
    }
  };

  const aiSender = {
    id: AI_AGENT_ID,
    name: AI_AGENT_NAME,
    avatarUrl: AI_AGENT_AVATAR_URL,
  };

  const chatUnavailableReason = {
    type: ChatEndedReasonTypes.CHAT_UNAVAILABLE,
    errorTitle: 'Unable to connect to chat',
    errorMessage:
      'There was an issue connecting to the chat service. Please try opening the chat again.',
  };

  const checkAvailability = async (): Promise<boolean> => {
    if (await shouldUseAnonymousSession()) {
      return Boolean(dfdHostUrl && dfdId);
    }
    const authToken = await getIdentityToken().getToken();
    return Boolean(authToken && dfdHostUrl && dfdId);
  };

  const initializeSDK = () => {
    if (!DFDChat.isInitialized) {
      DFDChat.initialize({
        id: dfdId,
        hostUrl: dfdHostUrl,
      });
    }
  };

  const clearDfdSessionState = () => {
    currentSession = null;
    pendingEventIdQueue.length = 0;
    correlationIdToEventOriginId.clear();
    leagueChatStarted = false;
  };

  /** `CHAT_UNAVAILABLE` + clear — use when chat never reached `CHAT_STARTED` (config / connect failure). */
  const emitChatUnavailableEnd = (
    handler: (event: AgentChatEvents) => void,
  ) => {
    handler({
      type: EventTypes.CHAT_ENDED,
      eventId: crypto.randomUUID(),
      reason: chatUnavailableReason,
    });
    clearDfdSessionState();
  };

  /** Recoverable error while chat is active; otherwise end as unavailable so League does not hang on `connecting`. */
  const emitRecoverableOrEndConnecting = (
    handler: (event: AgentChatEvents) => void,
    emitRecoverableError: (correlationId: string | undefined) => void,
    correlationId: string | undefined,
  ) => {
    if (leagueChatStarted) {
      emitRecoverableError(correlationId);
    } else {
      emitChatUnavailableEnd(handler);
    }
  };

  /**
   * DFD `ErrorReceived` → League. Uses `correlationId` when present to target the right message retry.
   * 401s arrive as `RefreshToken` events, not `ErrorReceived`.
   */
  const handleDfdErrorReceivedEvent = (
    sdkErrorType: DFDError,
    correlationId: string | undefined,
    handler: (event: AgentChatEvents) => void,
    emitRecoverableError: (correlationId: string | undefined) => void,
  ) => {
    if (sdkErrorType === DFDError.STATE_ERROR) {
      captureError(new Error('DFD STATE_ERROR'), {
        context: {
          Context: {
            sdkErrorType,
            correlationId: correlationId ?? 'none',
            pendingEventQueueDepth: pendingEventIdQueue.length,
            hasCurrentSession: Boolean(currentSession),
          },
        },
      });
      emitChatUnavailableEnd(handler);
      return;
    }

    // NETWORK_ERROR, UNKNOWN_ERROR, PARSING_ERROR, CANCELLED
    emitRecoverableOrEndConnecting(
      handler,
      emitRecoverableError,
      correlationId,
    );
  };

  const setupEventHandler = (
    session: ChatSession,
    handler: (event: AgentChatEvents) => void,
  ) => {
    const emitRecoverableError = (correlationId: string | undefined) => {
      if (correlationId) {
        const origin = correlationIdToEventOriginId.get(correlationId);
        if (origin) {
          correlationIdToEventOriginId.delete(correlationId);
          handler({
            type: EventTypes.ERROR_RECEIVED,
            eventId: crypto.randomUUID(),
            errorType: {
              type: ErrorTypes.MESSAGE_FAILED,
              eventOriginId: origin,
            },
          });
          return;
        }
      }

      handler({
        type: EventTypes.ERROR_RECEIVED,
        eventId: crypto.randomUUID(),
        errorType: {
          type: ErrorTypes.DEFAULT,
        },
      });
    };

    session.on(async (event: DFDBotEvent) => {
      switch (event.type) {
        case 'MessageAcknowledged': {
          if (event.correlationId) {
            const originId = pendingEventIdQueue.shift();
            if (originId) {
              correlationIdToEventOriginId.set(event.correlationId, originId);
            }
          }
          break;
        }

        case 'SessionStarted':
          leagueChatStarted = true;
          handler({
            type: EventTypes.CHAT_STARTED,
            eventId: crypto.randomUUID(),
          });
          break;

        case 'SessionEnded':
          clearDfdSessionState();
          handler({
            type: EventTypes.CHAT_ENDED,
            eventId: crypto.randomUUID(),
            reason: { type: ChatEndedReasonTypes.USER_DISCONNECTED },
          });
          onChatEnd?.();
          break;

        case 'SessionRestarted':
          leagueChatStarted = true;
          handler({
            type: EventTypes.CHAT_STARTED,
            eventId: crypto.randomUUID(),
          });
          break;

        case 'AgentProcessing':
          handler({
            type: EventTypes.AGENT_PROCESSING_STATE,
            eventId: crypto.randomUUID(),
            label: 'Thinking',
            isProcessing: event.isProcessing,
          });
          break;

        case 'MessageReceived': {
          if (event.correlationId) {
            correlationIdToEventOriginId.delete(event.correlationId);
          }
          handler({
            type: EventTypes.MESSAGE,
            eventId: crypto.randomUUID(),
            message: event.content ?? '',
            messageRichText: event.content ?? '',
            sender: event.participant
              ? {
                  id: event.participant.id,
                  name: event.participant.name,
                  avatarUrl: event.participant.avatar || AI_AGENT_AVATAR_URL,
                }
              : aiSender,
            timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
          });
          break;
        }

        case 'ParticipantJoined':
          handler({
            type: EventTypes.PARTICIPANT_JOINED,
            eventId: crypto.randomUUID(),
            participant: {
              id: event.participant.id,
              name: event.participant.name,
              avatarUrl: event.participant.avatar || AI_AGENT_AVATAR_URL,
            },
          });
          break;

        case 'ActionsUpdated': {
          handler({
            type: EventTypes.MESSAGE,
            eventId: crypto.randomUUID(),
            sender: aiSender,
            timestamp: new Date(),
            chips: event.actions.map((action: DfdAction) => {
              const chip = {
                label: action.label,
                value: action.id,
                metadata: {},
              };
              if (action.type === 'Link') {
                // SDK 0.5+ `ActionsUpdated`: Link actions use `link` only (see DFDActionsUpdatedLinkAction).
                return action.link
                  ? { ...chip, link: { value: action.link } }
                  : chip;
              }
              // Navigate | NavigateBackward | NavigateForward | NavigateUpward → chip uses callAction(id)
              return chip;
            }),
          });
          break;
        }

        case 'RefreshToken':
          try {
            const tokenManager = getIdentityToken();
            await tokenManager.refreshToken();
            const newToken = await tokenManager.getToken();
            if (!newToken) {
              throw new Error('Token refresh returned empty token');
            }
            await session.updateToken(newToken);
          } catch {
            emitRecoverableOrEndConnecting(
              handler,
              emitRecoverableError,
              undefined,
            );
          }
          break;

        case 'ErrorReceived':
          handleDfdErrorReceivedEvent(
            event.errorType,
            event.correlationId,
            handler,
            emitRecoverableError,
          );
          break;

        default:
          break;
      }
    });
  };

  const startAnonymousChat = async (
    handler: (event: AgentChatEvents) => void,
  ) => {
    if (!dfdHostUrl || !dfdId) {
      emitChatUnavailableEnd(handler);
      return;
    }

    initializeSDK();

    const session = DFDChat.createAnonymousSession({
      contentFormat: DFDContentFormat.HTML,
    });

    leagueChatStarted = false;
    currentSession = session;
    setupEventHandler(session, handler);
    await session.newChat();
  };

  const startAuthenticatedChat = async (
    handler: (event: AgentChatEvents) => void,
  ) => {
    const authToken = await getIdentityToken().getToken();
    if (!authToken || !dfdHostUrl || !dfdId) {
      emitChatUnavailableEnd(handler);
      return;
    }

    // Fetch user profile for demographics
    const profile = await getUserProfile();

    initializeSDK();

    const session = DFDChat.createAuthenticatedSession({
      authToken,
      contentFormat: DFDContentFormat.HTML,
      demographics: {
        user_id: profile.user_id,
        first_name: profile.user_profile?.first_name ?? '',
        last_name: profile.user_profile?.last_name ?? '',
        email_address: profile.email,
      },
    });

    leagueChatStarted = false;
    currentSession = session;
    setupEventHandler(session, handler);
    await session.newChat();
  };

  const startChat = async (handler: (event: AgentChatEvents) => void) => {
    if (await shouldUseAnonymousSession()) {
      await startAnonymousChat(handler);
    } else {
      await startAuthenticatedChat(handler);
    }
  };

  const sendMessage = async (message: string) => {
    if (currentSession) {
      await currentSession.sendMessage(message);
    }
  };

  const endChat = () => {
    if (currentSession) {
      currentSession.endChat();
      clearDfdSessionState();
    }
  };

  return {
    subscribeToAgentChatEvents: (handler: (event: AgentChatEvents) => void) => {
      storedHandler = handler;
      return null;
    },

    handleUserChatEvents: async (event: UserChatEvents) => {
      switch (event.type) {
        case EventTypes.START_CHAT:
          if (storedHandler) {
            try {
              if (await checkAvailability()) {
                await startChat(storedHandler);
              } else {
                emitChatUnavailableEnd(storedHandler);
              }
            } catch {
              emitChatUnavailableEnd(storedHandler);
            }
          }
          break;

        case EventTypes.MESSAGE:
          // SDK 0.5.0: sendMessage / callAction never throw — failures arrive as
          // `ErrorReceived` events with the matching `correlationId`.
          if (event?.message && event.message.trim().length > 0) {
            enqueuePendingEventId(event.eventId);
            await sendMessage(event.message);
          } else if (event?.chips?.length && currentSession) {
            enqueuePendingEventId(event.eventId);
            await currentSession.callAction(event.chips[0].value);
          }
          break;

        case EventTypes.END_CHAT:
          endChat();
          break;

        default:
          break;
      }
    },
  };
};

/**
 * Single live chat client that dynamically switches between
 * anonymous and authenticated sessions based on auth token availability.
 * - No valid token = anonymous session
 * - Valid token = authenticated session with user demographics
 */
export const dfdLiveChatClient = createLiveChatClient();
